/**
 * This interface describes an audio track that can contain
 * one or more audio channels. Ideally, we are using mono streams
 * so that each audio channel can be dealt with individually, for
 * instance for muting.
 */
export interface AudioTrackConfig {
  /**
   * If true, this track won't be played.
   */
  isMuted: boolean;

  /**
   * Soloing a track does not mean playing just this one. It means
   * muting every other track that is not itself marked as soloed.
   *
   * Let's say for instance that you have 16 tracks and you want to hear
   * only 2 and 5. Instead of have to mute manually other tracks except
   * these 2, you just mark tracks 2 and 5 as soloed.
   */
  isSolo: boolean;
}

/**
 *  This object describes a range of audio peak visualization normalized to a
 *  range of [0.0, 1.0] where 0.0 is the beginning of the audio buffer and
 *  1.0 is the end of the audio buffer.
 */
export interface PeakRange {
  start: number,
  end: number,
}

/**
 *  This object is used to describe changes to a list of AudioTrackConfigs
 */
export interface AudioTrackConfigUpdate {
  type: 'mute' | 'solo',
  trackIndex: number,
  value: boolean,
}

export function updateAudioTrackConfig(
  config: AudioTrackConfig[],
  updates: AudioTrackConfigUpdate[],
  defaultTrackConfig?: (trackIndex: number) => AudioTrackConfig,
): AudioTrackConfig[] {
  const updatedConfigs = config.map((c) => ({ isSolo: c.isSolo, isMuted: c.isMuted }));
  updates.forEach((u) => {
    while (updatedConfigs.length <= u.trackIndex) {
      if (!defaultTrackConfig) {
        return;
      }
      updatedConfigs.push(defaultTrackConfig(updatedConfigs.length));
    }
    const config = updatedConfigs[u.trackIndex];
    switch (u.type) {
      case 'mute': {
        config.isMuted = u.value;
        break;
      }
      case 'solo': {
        config.isSolo = u.value;
        break;
      }
    }
  });
  return updatedConfigs;
}

export function createMixMatrixFromAudioTrackConfig(configs: AudioTrackConfig[]): number[][] {
  const anySoloed = configs.some((config) => config.isSolo);
  return configs.map((config, trackIndex) => {
    const isOn = (!anySoloed || config.isSolo) && !config.isMuted;
    const row : number[] = [0.0, 0.0];
    row[trackIndex % 2] = isOn ? 1.0 : 0.0;
    return row;
  });
}


